import { useState, useEffect } from "react";
import { Button } from "@component/button";
import { Input } from "@component/input";
import { Label } from "@component/label";
import { Textarea } from "@component/textarea";
import { cn } from "@util/tailwind";
import FileUpload from "@component/file-upload";

interface CompanyProfileFormProps {
    onSubmit: (data: CompanyProfileData, file: File | null) => void;
    onBack: () => void;
    className?: string;
    initialData?: Partial<CompanyProfileData>;
}

export interface CompanyProfileData {
    companyName: string;
    companyDescription: string;
    companyLogo: File | null;
    companyLinkedin: string;
}

export default function CompanyProfileForm({
    onSubmit,
    onBack,
    className,
    initialData = {},
}: CompanyProfileFormProps) {
    const [formData, setFormData] = useState<CompanyProfileData>({
        companyName: initialData.companyName || "",
        companyDescription: initialData.companyDescription || "",
        companyLogo: initialData.companyLogo || null,
        companyLinkedin: initialData.companyLinkedin || "",
    });
    const [companyLogoFile, setCompanyLogoFile] = useState<File | null>(null);

    // Update form data when initialData changes
    useEffect(() => {
        setFormData({
            companyName: initialData.companyName || formData.companyName,
            companyDescription: initialData.companyDescription || formData.companyDescription,
            companyLogo: initialData.companyLogo || formData.companyLogo,
            companyLinkedin: initialData.companyLinkedin || formData.companyLinkedin,
        });
    }, [initialData]);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleLogoChange = async (file: File | null) => {
        if (!file) return;
        setCompanyLogoFile(file)
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(formData, companyLogoFile);
    };

    return (
        <form
            onSubmit={handleSubmit}
            className={cn("space-y-6", className)}
            data-pol-id="eg1lhk"
            data-pol-file-name="company-profile-form"
            data-pol-file-type="component"
        >
            <div
                className="space-y-4"
                data-pol-id="fli8bi"
                data-pol-file-name="company-profile-form"
                data-pol-file-type="component"
            >
                <div
                    className="space-y-2"
                    data-pol-id="0267dq"
                    data-pol-file-name="company-profile-form"
                    data-pol-file-type="component"
                >
                    <Label
                        htmlFor="companyName"
                        data-pol-id="9ej1ve"
                        data-pol-file-name="company-profile-form"
                        data-pol-file-type="component"
                    >
                        Company Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                        name="companyName"
                        placeholder="Enter company name"
                        value={formData.companyName}
                        onChange={handleChange}
                        required
                        data-pol-id="lcd4b1"
                        data-pol-file-name="company-profile-form"
                        data-pol-file-type="component"
                    />
                </div>

                <div
                    className="space-y-2"
                    data-pol-id="4zsjef"
                    data-pol-file-name="company-profile-form"
                    data-pol-file-type="component"
                >
                    <Label
                        htmlFor="companyDescription"
                        data-pol-id="vtgite"
                        data-pol-file-name="company-profile-form"
                        data-pol-file-type="component"
                    >
                        Company Description
                    </Label>
                    <Textarea
                        name="companyDescription"
                        placeholder="Describe your company"
                        value={formData.companyDescription}
                        onChange={handleChange}
                        rows={4}
                        data-pol-id="9i70hx"
                        data-pol-file-name="company-profile-form"
                        data-pol-file-type="component"
                    />
                </div>

                <div
                    className="space-y-2"
                    data-pol-id="obzq9t"
                    data-pol-file-name="company-profile-form"
                    data-pol-file-type="component"
                >
                    <Label
                        data-pol-id="9trb8e"
                        data-pol-file-name="company-profile-form"
                        data-pol-file-type="component"
                    >
                        Company Logo
                    </Label>
                    <FileUpload
                        label="Company Logo"
                        accept="image/*"
                        onChange={handleLogoChange}
                        value={companyLogoFile}
                        data-pol-id="9xbcfm"
                        data-pol-file-name="company-profile-form"
                        data-pol-file-type="component"
                    />
                </div>

                <div
                    className="space-y-2"
                    data-pol-id="lkicrl"
                    data-pol-file-name="company-profile-form"
                    data-pol-file-type="component"
                >
                    <Label
                        htmlFor="companyLinkedin"
                        data-pol-id="d7gew0"
                        data-pol-file-name="company-profile-form"
                        data-pol-file-type="component"
                    >
                        Company LinkedIn
                    </Label>
                    <Input
                        name="companyLinkedin"
                        placeholder="https://linkedin.com/company/your-company"
                        value={formData.companyLinkedin}
                        onChange={handleChange}
                        data-pol-id="gv0nfo"
                        data-pol-file-name="company-profile-form"
                        data-pol-file-type="component"
                    />
                </div>
            </div>

            <div
                className="flex gap-4"
                data-pol-id="fldon0"
                data-pol-file-name="company-profile-form"
                data-pol-file-type="component"
            >
                <Button
                    type="button"
                    variant="outline"
                    onClick={onBack}
                    className="flex-1"
                    data-pol-id="03mtqc"
                    data-pol-file-name="company-profile-form"
                    data-pol-file-type="component"
                >
                    Back
                </Button>
                <Button
                    type="submit"
                    className="flex-1 bg-blue-700 hover:bg-blue-800 text-white"
                    data-pol-id="omt787"
                    data-pol-file-name="company-profile-form"
                    data-pol-file-type="component"
                >
                    Submit
                </Button>
            </div>
        </form>
    );
}
